// StaffPage.jsx
// Route: /staff/:slug
// Staff only — PIN + GPS → shows today's QR 2 (fetched from backend)
// QR URL is always server-generated from current DynamoDB secret
// No client-side QR generation — no qrcode.react needed

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

const BIDS = { milkas: "biz-a63374ba-df71-48d5-b419-3447eedd6c8f" };

const C = {
  deepBlue: "#1a3a5c",
  midBlue:  "#2563a8",
  lightBlue:"#dbeafe",
  cream:    "#fdf6e3",
  saffron:  "#f5a623",
  white:    "#ffffff",
  textMid:  "#4a6580",
  error:    "#dc2626",
  border:   "#c8ddf0",
};

export default function StaffPage() {
  const { slug } = useParams();
  const businessId = BIDS[slug];

  const [phase, setPhase] = useState("pin"); // pin | loading | ready | error
  const [pin, setPin] = useState("");
  const [qr2Url, setQr2Url] = useState(null);
  const [shopName, setShopName] = useState("Milkas Dairy");
  const [testMode, setTestMode] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [checking, setChecking] = useState(false);

  // Auto-refresh QR every 30 minutes — QR rotates daily at 6AM
  useEffect(() => {
    if (phase !== "ready") return;
    const interval = setInterval(() => {
      const savedPin = localStorage.getItem(`staff_pin_${businessId}`);
      if (savedPin) fetchQR(savedPin, null, null, true);
    }, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [phase, businessId]);

  // Auto-login if PIN saved today
  useEffect(() => {
    if (!businessId) return;
    const savedPin = localStorage.getItem(`staff_pin_${businessId}`);
    const savedDate = localStorage.getItem(`staff_pin_date_${businessId}`);
    const today = new Date().toISOString().slice(0, 10);
    if (savedPin && savedDate === today) {
      setPin(savedPin);
      handleFetch(savedPin);
    }
  }, [businessId]);

  async function handleFetch(overridePin) {
    const usePin = overridePin || pin;
    if (!usePin || usePin.length < 4) return;
    setPhase("loading");
    setChecking(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => fetchQR(usePin, pos.coords.latitude, pos.coords.longitude),
        ()    => fetchQR(usePin, null, null), // GPS denied → server decides (testMode)
        { timeout: 8000, maximumAge: 60000 }
      );
    } else {
      fetchQR(usePin, null, null);
    }
  }

  async function fetchQR(usePin, lat, lng, silent = false) {
    if (!silent) setChecking(true);
    try {
      let url = `${API}/rewards/staff?businessId=${businessId}&pin=${usePin}`;
      if (lat != null) url += `&gpsLat=${lat}&gpsLng=${lng}`;

      const r = await fetch(url);
      const d = await r.json();

      if (r.ok) {
        setQr2Url(d.qr2Url); // URL built SERVER-SIDE from current secret
        setShopName(d.shopName || "Milkas Dairy");
        setTestMode(d.testMode || false);
        setLastRefresh(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }));
        localStorage.setItem(`staff_pin_${businessId}`, usePin);
        localStorage.setItem(`staff_pin_date_${businessId}`, new Date().toISOString().slice(0, 10));
        setPhase("ready");
      } else {
        setErrorMsg(d.error || "Access denied");
        setPhase("error");
      }
    } catch {
      setErrorMsg("Network error. Check internet connection.");
      setPhase("error");
    } finally {
      setChecking(false);
    }
  }

  if (!businessId) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.deepBlue, fontFamily: "system-ui" }}>
        <div style={{ color: C.white, textAlign: "center" }}>Shop not found.</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh", background: C.deepBlue,
      fontFamily: "'Nunito', system-ui, sans-serif",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", padding: 20,
    }}>

      {/* PIN Entry */}
      {(phase === "pin" || phase === "error") && (
        <div style={{ background: C.white, borderRadius: 20, padding: "32px 28px", width: "100%", maxWidth: 340, boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🥛</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: C.deepBlue }}>{shopName}</div>
            <div style={{ fontSize: 13, color: C.textMid, fontWeight: 600, marginTop: 4 }}>Staff Access</div>
          </div>

          {phase === "error" && (
            <div style={{ background: "#fef2f2", border: `1px solid ${C.error}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 700, color: C.error, marginBottom: 16, textAlign: "center" }}>
              {errorMsg}
            </div>
          )}

          <div style={{ fontSize: 11, fontWeight: 800, color: C.textMid, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>
            Enter Staff PIN
          </div>
          <input
            type="tel" maxLength={6} value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, ""))}
            onKeyDown={e => e.key === "Enter" && handleFetch()}
            placeholder="• • • •"
            style={{ width: "100%", padding: "14px 16px", border: `2px solid ${C.border}`, borderRadius: 12, fontSize: 28, fontWeight: 900, letterSpacing: 12, textAlign: "center", fontFamily: "inherit", color: C.deepBlue, background: C.cream, outline: "none", boxSizing: "border-box", marginBottom: 16 }}
          />
          <button
            onClick={() => handleFetch()}
            disabled={checking || pin.length < 4}
            style={{ width: "100%", padding: 14, background: pin.length >= 4 ? C.deepBlue : C.border, color: C.white, border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, fontFamily: "inherit", cursor: pin.length >= 4 ? "pointer" : "not-allowed" }}
          >
            {checking ? "Checking…" : "Open Staff View →"}
          </button>
        </div>
      )}

      {/* Loading */}
      {phase === "loading" && (
        <div style={{ textAlign: "center", color: C.white }}>
          <div style={{ width: 48, height: 48, border: "4px solid rgba(255,255,255,0.2)", borderTop: "4px solid white", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <div style={{ fontSize: 14, fontWeight: 600, opacity: 0.8 }}>Verifying…</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* QR Ready */}
      {phase === "ready" && qr2Url && (
        <div style={{ width: "100%", maxWidth: 400, textAlign: "center" }}>

          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>{shopName}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: C.white }}>Redemption QR</div>
            {testMode && (
              <div style={{ display: "inline-block", marginTop: 8, background: C.saffron, color: C.deepBlue, borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 800 }}>
                TEST MODE — GPS bypassed
              </div>
            )}
          </div>

          {/* Big QR — rendered as image from qrserver.com */}
          <div style={{ background: C.white, borderRadius: 24, padding: 24, display: "inline-block", boxShadow: "0 12px 48px rgba(0,0,0,0.4)", marginBottom: 20 }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&ecc=M&data=${encodeURIComponent(qr2Url)}`}
              alt="Redemption QR"
              style={{ width: 260, height: 260, display: "block", borderRadius: 8 }}
            />
          </div>

          {/* Instruction */}
          <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 14, padding: "14px 20px", marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.white, marginBottom: 4 }}>Show this to customer</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>Only when they want to redeem a reward</div>
          </div>

          {/* Footer */}
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontWeight: 600, marginBottom: 12 }}>
            Auto-rotates daily at 6 AM · Last loaded {lastRefresh}
          </div>

          {/* Manual refresh */}
          <button
            onClick={() => fetchQR(localStorage.getItem(`staff_pin_${businessId}`), null, null)}
            style={{ background: "none", border: "1.5px solid rgba(255,255,255,0.2)", borderRadius: 10, padding: "8px 20px", fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.6)", cursor: "pointer", fontFamily: "inherit" }}
          >
            ↻ Refresh QR
          </button>
        </div>
      )}
    </div>
  );
}
